import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChildProfile } from './ChildProfileLayout';
import { profilesApi } from '../../api/endpoints/profiles';
import {
    FiUser,
    FiEdit2,
    FiTrash2,
    FiMapPin,
    FiUsers,
    FiPhone,
    FiArrowRight,
} from 'react-icons/fi';
import { GiBabyBottle } from 'react-icons/gi';
import {
    ConfirmDialog,
    EmptyState,
    SkeletonHeaderCard,
    SkeletonCard,
    useToast,
} from '../../theme/components';
import { PageHeader } from '../../theme/components/PageHeader';
import { PageButton } from '../../theme/components/PageButton';
import { parseApiError } from '../../api/errors';
import { formatDateDisplay } from '../../utils/dates';
import { APP_NAME } from '../../config';
import { buildPdfReport } from '../../utils/pdfReport';
import './ChildProfile.css';

const Field = ({ label, children }) => (
    <div className="cp-data-group">
        <label>{label}</label>
        <p>{children || '—'}</p>
    </div>
);

const ChildProfile = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const { data: profile, loading, error, refetch, setPdfExporter } = useChildProfile();
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Pull the joined display name once so the PDF exporter doesn't
    // recompute it on every keystroke. The header card renders the same
    // string; mirror its logic here so the PDF matches what's on screen.
    const fullName = useMemo(() => {
        if (!profile) return '';
        if (profile.surname) {
            return `${profile.surname} ${profile.firstName || ''}`.trim();
        }
        return profile.name || '';
    }, [profile]);

    // Personal-info PDF export — four tables mirroring the on-screen
    // cards (Personal / Birth / Family / Location). The header's
    // Export PDF button calls this; the other tabs register their own
    // exporters so each tab exports what's visible there.
    const generatePDF = useCallback(() => {
        if (!profile) return;
        const safe = (v) => (v === null || v === undefined || v === '' ? '-' : v);
        const formattedAddress = profile.address
            ? profile.address.split(', ').join(', ')
            : '-';

        buildPdfReport({
            title: 'Child Profile Report',
            subtitle: `${fullName} · ${safe(profile.childNo)}`,
            filters: [],
            orientation: 'portrait',
            sections: [
                {
                    heading: 'Personal Information',
                    head: ['Field', 'Value'],
                    body: [
                        ['Full Name', fullName || '-'],
                        ['Registration No', safe(profile.childNo)],
                        ['Gender', safe(profile.gender)],
                        ['Date of Birth', formatDateDisplay(profile.dob)],
                        ['Registered Date', formatDateDisplay(profile.registeredDate)],
                    ],
                    accent: [14, 165, 233],
                },
                {
                    heading: 'Birth Statistics',
                    head: ['Field', 'Value'],
                    body: [
                        ['Birth Weight', profile.birthWeight ? `${profile.birthWeight} kg` : '-'],
                        ['Birth Height', profile.birthHeight ? `${profile.birthHeight} cm` : '-'],
                        ['Way of Delivery', safe(profile.deliveryWay)],
                        ['Gestational Age', safe(profile.gestationalAge)],
                        ['Birth Hospital', safe(profile.birthHospital)],
                    ],
                    accent: [236, 72, 153],
                },
                {
                    heading: 'Family Details',
                    head: ['Field', 'Value'],
                    body: [
                        ["Mother's Name", safe(profile.mothersName)],
                        ["Mother's No", safe(profile.mothersNo)],
                        ["Mother's DOB", formatDateDisplay(profile.mothersDob)],
                        ['Total Children', safe(profile.totalChildren)],
                        ["Father's Name", safe(profile.fathersName)],
                        ['Contact Number', safe(profile.contactNumber)],
                    ],
                    accent: [34, 197, 94],
                },
                {
                    heading: 'Location',
                    head: ['Field', 'Value'],
                    body: [
                        ['Address', formattedAddress],
                        ['District', safe(profile.district)],
                        ['GN Division', safe(profile.gnDevision)],
                    ],
                    accent: [234, 179, 8],
                },
            ],
            fileName: `${profile.childNo || 'Child'}_Profile_${new Date().toISOString().split('T')[0]}.pdf`,
            toast,
            emptyMessage: 'No profile data to export.',
        });
    }, [profile, fullName, toast]);

    // Register this tab's PDF exporter with the layout so the header's
    // "Export PDF" button calls it. Cleared on unmount so leaving the
    // tab doesn't leak the callback into another tab's exporter.
    useEffect(() => {
        setPdfExporter(() => generatePDF());
        return () => setPdfExporter(null);
    }, [setPdfExporter, generatePDF]);

    const handleDeleteRecord = async () => {
        setDeleting(true);
        try {
            await profilesApi.remove(id);
            toast.success('Child record deleted.');
            navigate('/profiles');
        } catch (err) {
            toast.error(parseApiError(err, 'Failed to delete profile.'));
            setConfirmDelete(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleEditProfile = () => {
        if (!profile) return;
        let fName = profile.firstName || '';
        let sName = profile.surname || '';

        if (!fName && !sName && profile.name) {
            const parts = profile.name.split(' ');
            if (parts.length > 1) {
                sName = parts.pop();
                fName = parts.join(' ');
            } else {
                fName = profile.name;
            }
        }

        const mappedChildData = {
            id: profile.id,
            childNo: profile.childNo || '',
            surname: sName,
            firstName: fName,
            gender: profile.gender || '',
            birthDate: profile.dob || '',
            birthWeight: profile.birthWeight || '',
            birthHeight: profile.birthHeight || '',
            birthHospital: profile.birthHospital || '',
            district: profile.district || '',
            gnDevision: profile.gnDevision || '',
            registeredDate: profile.registeredDate || '',
            address: profile.address || '',
            deliveryWay: profile.deliveryWay || '',
            gestationalAge: profile.gestationalAge || '',
        };

        const mappedFamilyData = {
            mothersNo: profile.mothersNo || '',
            mothersName: profile.mothersName || '',
            mothersDob: profile.mothersDob || '',
            totalChildren: profile.totalChildren || '',
            fathersName: profile.fathersName || '',
            contactNumber: profile.contactNumber || '',
        };

        navigate('/add-child', {
            state: {
                childData: mappedChildData,
                familyData: mappedFamilyData,
                // ponytail: pass originals so ReviewAndFinish can build a
                // diff and PATCH only the fields the user actually changed.
                originalChildData: mappedChildData,
                originalFamilyData: mappedFamilyData,
                isEditMode: true,
            },
        });
    };

    if (loading) {
        return (
            <div className="cp-wrapper">
                <div className="cp-skeleton-stack">
                    <SkeletonHeaderCard />
                    <div className="cp-skeleton-cols">
                        <div>
                            <SkeletonCard height={180} />
                            <SkeletonCard height={180} />
                        </div>
                        <div>
                            <SkeletonCard height={180} />
                            <SkeletonCard height={180} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="cp-wrapper">
                <EmptyState
                    illustration="error"
                    title="Could not load this profile"
                    description={parseApiError(error, 'Unknown error')}
                    action={
                        <PageButton
                            type="button"
                            variant="primary"
                            onClick={() => refetch?.()}
                        >
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="cp-wrapper">
                <EmptyState
                    illustration="no-data"
                    title="Profile not found"
                    description="This child record may have been removed or never existed."
                    action={
                        <PageButton
                            type="button"
                            variant="primary"
                            endIcon={<FiArrowRight size={14} />}
                            onClick={() => navigate('/profiles')}
                        >
                            Back to profiles
                        </PageButton>
                    }
                />
            </div>
        );
    }

    const formattedAddress = profile.address
        ? profile.address.split(', ').join(',\n')
        : '';

    return (
        <div className="cp-wrapper">
            <PageHeader
                title="Child details"
                subtitle="Personal, family, and location information for this child."
                icon={FiUser}
                className="cp-section-header"
            />

            <div className="cp-content-grid">
                <div className="cp-col-left">
                    <div className="cp-info-card">
                        <div className="cp-card-header">
                            <div className="cp-card-title">
                                <FiUser className="cp-card-icon" /> Personal Information
                            </div>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    gap: 8,
                                    alignItems: 'center',
                                }}
                            >
                                <span className="cp-card-badge">CHILD DETAILS</span>
                                <PageButton
                                    type="button"
                                    variant="ghost"
                                    size="small"
                                    startIcon={<FiTrash2 size={12} />}
                                    onClick={() => setConfirmDelete(true)}
                                    sx={{
                                        color: 'var(--pt-error)',
                                        borderColor:
                                            'color-mix(in srgb, var(--pt-error) 30%, transparent)',
                                    }}
                                >
                                    Delete
                                </PageButton>
                                <PageButton
                                    type="button"
                                    variant="primary"
                                    size="small"
                                    startIcon={<FiEdit2 size={12} />}
                                    onClick={handleEditProfile}
                                >
                                    Edit
                                </PageButton>
                            </span>
                        </div>
                        <div className="cp-card-body">
                            <div className="cp-data-row">
                                <Field label="FULL NAME">{fullName}</Field>
                                <Field label="GENDER">{profile.gender}</Field>
                            </div>
                            <div className="cp-data-row">
                                <Field label="DATE OF BIRTH">{formatDateDisplay(profile.dob)}</Field>
                                <Field label="REGISTERED DATE">{formatDateDisplay(profile.registeredDate)}</Field>
                            </div>
                        </div>
                    </div>

                    <div className="cp-info-card">
                        <div className="cp-card-header">
                            <div className="cp-card-title">
                                <GiBabyBottle className="cp-card-icon" /> Birth Statistics
                            </div>
                            <span className="cp-card-badge">MEDICAL DATA</span>
                        </div>
                        <div className="cp-card-body">
                            <div className="cp-stats-grid">
                                <div className="cp-stat-box">
                                    <span className="cp-stat-label">Birth Weight</span>
                                    <span className="cp-stat-value">{profile.birthWeight ? `${profile.birthWeight} kg` : '—'}</span>
                                </div>
                                <div className="cp-stat-box">
                                    <span className="cp-stat-label">Birth Height</span>
                                    <span className="cp-stat-value">{profile.birthHeight ? `${profile.birthHeight} cm` : '—'}</span>
                                </div>
                                <div className="cp-stat-box">
                                    <span className="cp-stat-label">Way of Delivery</span>
                                    <span className="cp-stat-value text-dark">{profile.deliveryWay || '—'}</span>
                                </div>
                                <div className="cp-stat-box">
                                    <span className="cp-stat-label">Gestational Age</span>
                                    <span className="cp-stat-value text-dark">{profile.gestationalAge || '—'}</span>
                                </div>
                            </div>
                            <div className="cp-data-group mt-4">
                                <label>BIRTH HOSPITAL</label>
                                <p className="fw-600 text-dark">{profile.birthHospital || '—'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="cp-col-right">
                    <div className="cp-info-card">
                        <div className="cp-card-header no-border">
                            <div className="cp-card-title">
                                <FiUsers className="cp-card-icon" /> Family Details
                            </div>
                        </div>
                        <div className="cp-card-body pt-0">
                            <div className="cp-data-group mb-4">
                                <label>MOTHER'S NAME</label>
                                <p>{profile.mothersName || '—'}</p>
                            </div>
                            <div className="cp-data-row mb-4">
                                <Field label="MOTHER'S NO">{profile.mothersNo}</Field>
                                <Field label="MOTHER'S DOB">{formatDateDisplay(profile.mothersDob)}</Field>
                            </div>
                            <div className="cp-data-group mb-4">
                                <label>TOTAL CHILDREN</label>
                                <p>{profile.totalChildren || '—'}</p>
                            </div>
                            <div className="cp-data-group mb-4">
                                <label>FATHER'S NAME</label>
                                <p>{profile.fathersName || '—'}</p>
                            </div>
                            <div className="cp-data-group">
                                <label>CONTACT NUMBER</label>
                                <p className="cp-contact-link">
                                    <FiPhone size={14} />
                                    {profile.contactNumber ? (
                                        <a href={`tel:${profile.contactNumber}`}>{profile.contactNumber}</a>
                                    ) : (
                                        <span>—</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="cp-info-card">
                        <div className="cp-card-header no-border">
                            <div className="cp-card-title">
                                <FiMapPin className="cp-card-icon" /> Location Info
                            </div>
                        </div>
                        <div className="cp-card-body pt-0">
                            <div className="cp-data-group mb-4">
                                <label>ADDRESS</label>
                                <p className="cp-address">{formattedAddress || '—'}</p>
                            </div>
                            <div className="cp-data-group mb-4">
                                <label>DISTRICT (RESIDENCE)</label>
                                <p>{profile.district || '—'}</p>
                            </div>
                            <div className="cp-data-row">
                                <div className="cp-data-group">
                                    <label>GN DEVISION (RESIDENCE)</label>
                                    <p>{profile.gnDevision || '—'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmDialog
                open={confirmDelete}
                title="Delete this child's record?"
                body={
                    <div>
                        <p>This will permanently remove <strong>{fullName}</strong> from the registry, including all vaccination and growth history.</p>
                        <p style={{ marginTop: 12, color: 'var(--pt-text-secondary)', fontSize: '0.88rem' }}>
                            This action cannot be undone.
                        </p>
                    </div>
                }
                confirmLabel={deleting ? 'Deleting…' : 'Delete'}
                confirmSeverity="error"
                onConfirm={handleDeleteRecord}
                onClose={() => (deleting ? null : setConfirmDelete(false))}
            />
        </div>
    );
};

export default ChildProfile;
